import { useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { ApiError } from '@/lib/api/client'
import { showErrorToast } from '@/lib/toast'

import { WinnerCreating } from './WinnerCreating'
import { WinnerDropZone } from './WinnerDropZone'
import { WinnerImportDone } from './WinnerImportDone'
import { WinnerProgressHeader } from './WinnerProgressHeader'
import { WinnerReading } from './WinnerReading'
import { WinnerReview } from './WinnerReview'
import {
  alreadyImportedFrom, commitWinnerImport, previewWinnerImport,
  type WinnerImportAlreadyImported, type WinnerImportCustomerDecision, type WinnerImportPreview,
  type WinnerImportProjectDecision, type WinnerImportResult,
} from './winner-import.api'
import {
  alreadyImportedMessage, alreadyImportedTarget, importProgress, narration, projectAfterCustomerChange, reviewBlocks,
  type ReviewBlock, type WinnerImportStage,
} from './winner-import-progress'
import { categoryDecisions, initialCategoryChoices, type CategoryChoice } from './winner-categories'
import { initialSupplyChoices, supplyDecisions, type SupplyChoice } from './winner-supply'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Set when the import starts inside a project: the quote lands there. */
  entryProject?: { id: string; name: string | null }
  onImported?: (result: WinnerImportResult) => void
}

const MIN_CREATING_MS = 1800

export function WinnerImportDialog({ open, onOpenChange, entryProject, onImported }: Props) {
  const navigate = useNavigate()
  const [stage, setStage] = useState<WinnerImportStage>('drop')
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<WinnerImportPreview | null>(null)
  const [stopMessage, setStopMessage] = useState<string | null>(null)
  // Set when the stop is « this exact file was already imported »: the screen links to that quote.
  const [already, setAlready] = useState<WinnerImportAlreadyImported | null>(null)
  const [customer, setCustomer] = useState<WinnerImportCustomerDecision | null>(null)
  const [project, setProject] = useState<WinnerImportProjectDecision | null>(null)
  const [categoryChoices, setCategoryChoices] = useState<Record<string, CategoryChoice>>({})
  const [supplyChoices, setSupplyChoices] = useState<Record<string, SupplyChoice>>({})
  const [confirmed, setConfirmed] = useState<ReadonlySet<ReviewBlock>>(new Set())
  const [openBlock, setOpenBlock] = useState<ReviewBlock | null>(null)
  const [result, setResult] = useState<WinnerImportResult | null>(null)
  const startedAt = useRef(0)
  // Every file read is one attempt; an answer for an older attempt (closed, or another file picked
  // since) is dropped instead of filling the review of the current one.
  const attempt = useRef(0)
  const [elapsedMs, setElapsedMs] = useState(0)

  const blocks = reviewBlocks(!!entryProject, preview?.categories?.needsReview === true, preview?.supply?.needsReview === true)
  const progress = importProgress(stage, confirmed.size, blocks.length)
  const steps = useMemo(() => (preview ? narration(preview) : null), [preview])

  const reset = () => {
    attempt.current += 1
    setStage('drop'); setFile(null); setPreview(null); setStopMessage(null); setAlready(null); setCustomer(null)
    setProject(null); setCategoryChoices({}); setSupplyChoices({}); setConfirmed(new Set()); setOpenBlock(null); setResult(null)
  }

  const stopAsAlreadyImported = (seen: WinnerImportAlreadyImported) => {
    setAlready(seen)
    setStopMessage(alreadyImportedMessage(seen))
    setStage('stopped')
  }

  const read = async (picked: File) => {
    const current = ++attempt.current
    setFile(picked)
    setStage('reading')
    startedAt.current = Date.now()
    try {
      const read = await previewWinnerImport(picked, entryProject?.id)
      if (current !== attempt.current) return
      if (read.stopReason) {
        setStopMessage(read.stopMessage ?? 'Ce fichier ne peut pas être importé.')
        setStage('stopped')
        return
      }
      if (read.alreadyImported?.sameFile) {
        stopAsAlreadyImported(read.alreadyImported)
        return
      }
      setPreview(read)
      setCustomer(initialCustomer(read))
      setProject(initialProject(read))
      setCategoryChoices(initialCategoryChoices(read.categories))
      setSupplyChoices(initialSupplyChoices(read.supply))
    } catch (error) {
      if (current !== attempt.current) return
      setStopMessage(error instanceof ApiError ? error.message : "Impossible de lire l'export Winner.")
      setStage('stopped')
    }
  }

  const startReview = () => {
    setStage('review')
    setOpenBlock(blocks[0])
  }

  const confirm = (block: ReviewBlock) => {
    const next = new Set(confirmed).add(block)
    setConfirmed(next)
    setOpenBlock(blocks.find((b) => !next.has(b)) ?? null)
  }

  const edit = (block: ReviewBlock) => {
    const next = new Set(confirmed)
    next.delete(block)
    // The job site depends on the customer: changing who it is reopens where it goes.
    if (block === 'customer') next.delete('project')
    setConfirmed(next)
    setOpenBlock(block)
  }

  const create = async () => {
    if (!file || !preview || !customer || !project) return
    const current = attempt.current
    setStage('creating')
    const minimum = new Promise((resolve) => window.setTimeout(resolve, MIN_CREATING_MS))
    try {
      const [created] = await Promise.all([
        commitWinnerImport(file, {
          ...(entryProject ? { entryProjectId: entryProject.id } : { customer, project }),
          categories: categoryDecisions(categoryChoices, preview.categories),
          supply: supplyDecisions(supplyChoices, preview.supply),
        }),
        minimum,
      ])
      if (current !== attempt.current) return
      setElapsedMs(Date.now() - startedAt.current)
      setResult(created)
      setStage('done')
      onImported?.(created)
    } catch (error) {
      if (current !== attempt.current) return
      // Imported meanwhile (another tab, a double submit): nothing was created by this attempt.
      const seen = alreadyImportedFrom(error)
      if (seen) {
        stopAsAlreadyImported(seen)
        return
      }
      // The server answered with a refusal: its transaction rolled back. No answer at all (network,
      // timeout) says nothing about what was created, so the screen must not promise it.
      // A 5xx or a 408 (gateway and proxy timeouts) is no answer either.
      showErrorToast(
        error instanceof ApiError && error.status < 500 && error.status !== 408
          ? error.message
          : 'Soly n’a pas reçu de réponse. Le dossier a peut-être été créé : vérifiez les chantiers avant de relancer.'
      )
      setStage('review')
    }
  }

  const close = (next: boolean) => {
    onOpenChange(next)
    if (!next) window.setTimeout(reset, 200)
  }

  const creatingSteps = entryProject
    ? ['Lignes du devis', `${preview?.quote?.articleCount ?? 0} articles vérifiés`, 'Devis rangé dans le chantier']
    : [
        customer?.mode === 'existing' ? 'Client rattaché' : 'Client créé',
        project?.mode === 'existing' ? 'Chantier rattaché' : 'Chantier créé',
        `${preview?.quote?.articleCount ?? 0} lignes de devis`,
        'Totaux contrôlés au cent',
      ]

  const subtitle = entryProject?.name
    ? `Dans le chantier « ${entryProject.name} »`
    : file ? file.name : 'Client, chantier et devis en une fois'

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        showCloseButton={false}
        data-testid="winner-import-dialog"
        className="flex h-[100dvh] max-w-none flex-col gap-0 overflow-hidden rounded-none border-0 p-0 sm:max-w-none"
      >
        <DialogTitle className="sr-only">Importer depuis Winner</DialogTitle>
        <DialogDescription className="sr-only">Lecture d&apos;un export Winner et création du dossier</DialogDescription>
        <div className="relative">
          <WinnerProgressHeader stage={stage} progress={progress} subtitle={subtitle} />
          <Button variant="ghost" size="icon" className="absolute right-3 top-3" onClick={() => close(false)} data-testid="winner-import-close-btn" aria-label="Fermer">
            <X className="h-5 w-5" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto bg-slate-50/40 px-6">
          {stage === 'drop' && <WinnerDropZone busy={false} intoProjectName={entryProject?.name} onFile={read} />}
          {stage === 'reading' && <WinnerReading fileName={file?.name ?? ''} steps={steps} onContinue={startReview} />}
          {stage === 'stopped' && (
            <div className="mx-auto max-w-xl space-y-4 py-16 text-center" data-testid="winner-import-stopped">
              <h3 className="text-xl font-semibold">{already ? 'Déjà importé' : 'Ce fichier ne peut pas être importé'}</h3>
              <p className="text-muted-foreground" data-testid="winner-import-stopped-message">{stopMessage}</p>
              {already && <p className="text-sm text-muted-foreground">Rien n&apos;a été créé.</p>}
              <div className="flex flex-wrap justify-center gap-2">
                {already && alreadyImportedTarget(already) && (
                  <Button
                    data-testid="winner-import-open-existing-btn"
                    onClick={() => {
                      const target = alreadyImportedTarget(already)!
                      close(false)
                      navigate(target)
                    }}
                  >
                    Voir le devis
                  </Button>
                )}
                <Button variant={already ? 'outline' : 'default'} onClick={reset} data-testid="winner-import-retry-btn">Choisir un autre fichier</Button>
              </div>
            </div>
          )}
          {stage === 'review' && preview && customer && project && (
            <WinnerReview
              preview={preview}
              blocks={blocks}
              confirmed={confirmed}
              openBlock={openBlock}
              customer={customer}
              project={project}
              busy={false}
              onCustomerChange={(next) => {
                setProject((current) => (current ? projectAfterCustomerChange(customer, next, current) : current))
                setCustomer(next)
              }}
              onProjectChange={setProject}
              categoryChoices={categoryChoices}
              onCategoryChange={(key, choice) => setCategoryChoices((current) => ({ ...current, [key]: choice }))}
              supplyChoices={supplyChoices}
              onSupplyChange={(key, choice) => setSupplyChoices((current) => ({ ...current, [key]: choice }))}
              onConfirm={confirm}
              onEdit={edit}
              onCreate={create}
            />
          )}
          {stage === 'creating' && <WinnerCreating steps={creatingSteps} finished={false} />}
          {stage === 'done' && result && (
            <WinnerImportDone
              result={result}
              elapsedMs={elapsedMs}
              suppliers={preview?.quote?.suppliers.map((s) => s.name) ?? []}
              onOpenQuote={() => {
                close(false)
                navigate(`/projets/${result.projectId}/devis-v2/${result.quoteId}/versions/${result.versionId}/edit`)
              }}
              onOpenProject={() => {
                close(false)
                navigate(`/projects/${result.projectId}`)
              }}
              onAnother={reset}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

function initialCustomer(preview: WinnerImportPreview): WinnerImportCustomerDecision {
  const winner = preview.customer!
  const best = preview.customerCandidates[0]
  const base: WinnerImportCustomerDecision = {
    mode: 'new',
    isCompany: winner.isCompany,
    name: winner.displayName,
    firstName: winner.firstName ?? '',
    lastName: winner.lastName ?? '',
    email: winner.email ?? '',
    phone: winner.phone ?? '',
    vatNumber: winner.vatNumber ?? '',
    address: winner.address,
  }
  return best && best.score >= 70 ? { ...base, mode: 'existing', customerId: best.customerId, name: best.name } : base
}

function initialProject(preview: WinnerImportPreview): WinnerImportProjectDecision {
  return {
    mode: 'new',
    name: preview.project?.suggestedName ?? '',
    projectTypeId: preview.suggestedProjectTypeId,
    jobSiteAddress: preview.project?.address ?? preview.customer?.address ?? null,
  }
}
